import { Button, Flex, Heading, SimpleGrid, useToast } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { SectionBox } from '@/components/SectionBox';
import { InputCpfCnpj, InputPhone } from '@/components/ControlledInput';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { InputField } from '@/components/InputField';
import { queryClient } from '@/services/api/queryClient';
import { ModalFormData, modalFormSchema } from './schemas';
import { usePostDashboardUserMutation } from '@/services/queries/dashboard-user';
import { t } from '@/i18n';

export const NewAccessControlUser = () => {
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ModalFormData>({
    resolver: zodResolver(modalFormSchema),
    defaultValues: {
      name: '',
      document: '',
      phone_number: '',
      email: '',
      email_confirmation: '',
    },
  });
  const toast = useToast();

  const postItemMutation = usePostDashboardUserMutation();

  const onSubmit = async (data: ModalFormData) => {
    postItemMutation.mutate(
      {
        name: data.name,
        document: data.document,
        phone_number: data.phone_number,
        email: data.email,
        email_confirmation: data.email_confirmation,
      },
      {
        onSuccess: async () => {
          reset();

          await queryClient.invalidateQueries(['GET_DASHBOARD_USER']);
          toast({
            title: t('pages.access_control.components.user.success_toast'),
            status: 'success',
          });
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title: (resp && resp.message) || t('pages.access_control.components.user.error_toast'),
          });
        },
      },
    );
  };

  return (
    <SectionBox bodyProps={{ gap: 'small' }}>
      <Heading variant='sectionTitle' fontWeight='semibold'>
        {t('pages.access_control.components.user.heading')}
      </Heading>

      <Flex
        as='form'
        onSubmit={handleSubmit(onSubmit)}
        direction={{ base: 'column', md: 'row' }}
        justifyContent={{ md: 'space-between' }}
        gap={12}
      >
        <SimpleGrid flex={1} maxW='1200px' columns={{ base: 1, sm: 2, md: 3, xl: 5 }} gap={6}>
          <InputField
            withoutFloating
            label={t('pages.access_control.components.user.form.name')}
            {...register('name')}
            rootProps={{ isInvalid: !!errors?.name, errorMessage: errors?.name?.message }}
          />

          <InputCpfCnpj
            withoutFloating
            control={control}
            name='document'
            label={t('pages.access_control.components.user.form.document')}
            rootProps={{ isInvalid: !!errors?.document, errorMessage: errors?.document?.message }}
          />

          <InputPhone
            withoutFloating
            control={control}
            name='phone_number'
            label={t('pages.access_control.components.user.form.phone')}
            rootProps={{ isInvalid: !!errors?.phone_number, errorMessage: errors?.phone_number?.message }}
          />

          <InputField
            withoutFloating
            label={t('pages.access_control.components.user.form.email')}
            {...register('email')}
            rootProps={{ isInvalid: !!errors?.email, errorMessage: errors?.email?.message }}
          />
          <InputField
            withoutFloating
            label={t('pages.access_control.components.user.form.email_confirmation')}
            {...register('email_confirmation')}
            rootProps={{ isInvalid: !!errors?.email_confirmation, errorMessage: errors?.email_confirmation?.message }}
          />
        </SimpleGrid>

        <Button alignSelf={{ md: 'flex-end' }} type='submit'>
          {t('pages.access_control.components.user.form.button_submit')}
        </Button>
      </Flex>

      <CenteredSpinner isLoading={postItemMutation.isLoading} />
    </SectionBox>
  );
};
